import { useState } from 'react'
import { Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FacebookLeadsTemplateHelpDialog } from '@/components/settings/FacebookLeadsTemplateHelpDialog'
import {
  type FacebookMappingSourceOption,
  type FacebookVariableMappingEntry,
  mappingEntryToSelectValue,
  parseMappingSelectValue,
} from '@/components/settings/facebookTemplateMapping'

type FacebookTemplateVariableMappingEditorProps = {
  mapping: FacebookVariableMappingEntry[]
  sourceOptions: FacebookMappingSourceOption[]
  onChange: (mapping: FacebookVariableMappingEntry[]) => void
  description?: string
  showHelp?: boolean
}

export function FacebookTemplateVariableMappingEditor({
  mapping,
  sourceOptions,
  onChange,
  description = 'Asigna cada hueco de la plantilla a un campo del lead o del formulario.',
  showHelp = false,
}: FacebookTemplateVariableMappingEditorProps) {
  const [helpOpen, setHelpOpen] = useState(false)

  if (mapping.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Esta plantilla no tiene variables en el cuerpo. Se enviará tal cual cuando llegue un lead.
      </p>
    )
  }

  const handleChange = (position: number, value: string) => {
    const parsed = parseMappingSelectValue(value)
    onChange(
      mapping.map((entry) =>
        entry.position === position
          ? { ...entry, source_type: parsed.source_type, source_key: parsed.source_key }
          : entry,
      ),
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label>Mapa de variables</Label>
        {showHelp ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="shrink-0 rounded-full text-muted-foreground"
            aria-label="Ayuda sobre el orden de variables"
            onClick={() => setHelpOpen(true)}
          >
            <Info className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
      {mapping.map((entry) => (
        <div key={entry.position} className="grid grid-cols-[80px_1fr] items-center gap-2">
          <span className="text-sm font-medium">{`{{${entry.position}}}`}</span>
          <Select
            value={mappingEntryToSelectValue(entry) || undefined}
            onValueChange={(value) => handleChange(entry.position, value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar campo" />
            </SelectTrigger>
            <SelectContent>
              {sourceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
      {showHelp ? (
        <FacebookLeadsTemplateHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      ) : null}
    </div>
  )
}
